import { Loader2 } from 'lucide-react';
import { ICON_SIZE } from '../../constants/icons';
import { CONTENT_LIST_PAGE_COPY } from '../lib/contentListPageConstants';
import type { InfiniteListPatternProps } from '../lib/contentListPageTypes';
import type { InfiniteListPatternState } from '../lib/infiniteListPatternState';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel';
import { SiteGridFadeIn } from './SiteFadeIn';

interface InfiniteListPatternBodyProps {
  listProps: InfiniteListPatternProps;
  state: InfiniteListPatternState;
  loadMore: () => void;
}

export function InfiniteListPatternBody({
  listProps,
  state,
  loadMore,
}: InfiniteListPatternBodyProps) {
  const sentinelRef = useInfiniteScrollSentinel({
    enabled: state.hasMore,
    loading: state.status === 'loadingMore',
    onIntersect: loadMore,
  });

  if (state.status === 'loading') {
    return null;
  }

  if (state.status === 'error') {
    return (
      <p className="text-body text-status-error">
        {state.errorMessage ?? CONTENT_LIST_PAGE_COPY.defaultError}
      </p>
    );
  }

  if (state.status === 'empty') {
    return (
      <p className="text-body text-text-tertiary">
        {listProps.emptyMessage}
      </p>
    );
  }

  if (state.status === 'success' || state.status === 'loadingMore') {
    return (
      <div className="col-span-full flex flex-col gap-10">
        <SiteGridFadeIn>
          {listProps.renderItems(state.items)}
        </SiteGridFadeIn>

        {state.status === 'loadingMore' && (
          <div className="flex items-center justify-center py-6">
            <Loader2
              size={ICON_SIZE.md}
              className="animate-spin text-text-quaternary"
              aria-hidden
            />
            <span className="sr-only">{CONTENT_LIST_PAGE_COPY.loading}</span>
          </div>
        )}

        {state.loadMoreErrorMessage && (
          <p className="text-body text-status-error text-center">
            {state.loadMoreErrorMessage}
          </p>
        )}

        {state.hasMore && <div ref={sentinelRef} className="h-1" aria-hidden />}
      </div>
    );
  }

  return null;
}
